import React from 'react';
import { Cloud, CloudOff, Check, Loader2 } from 'lucide-react';
import type { SaveStatus } from '@/hooks';

interface SaveIndicatorProps {
  status: SaveStatus;
  lastSaved: Date | null;
  error: string | null;
  onSave?: () => void;
}

export function SaveIndicator({ status, lastSaved, error, onSave }: SaveIndicatorProps) {
  // Don't show anything if idle and never saved (no project selected)
  if (status === 'idle' && !lastSaved) {
    return null;
  }

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex items-center gap-2 text-xs">
      {status === 'saving' && (
        <>
          <Loader2 size={14} className="animate-spin text-fg-muted" />
          <span className="text-fg-muted">Saving...</span>
        </>
      )}

      {status === 'pending' && (
        <>
          <Cloud size={14} className="text-fg-subtle" />
          <span className="text-fg-subtle">Unsaved changes</span>
        </>
      )}

      {status === 'saved' && (
        <>
          <Check size={14} className="text-green-400" />
          <span className="text-green-400">Saved</span>
        </>
      )}

      {status === 'idle' && lastSaved && (
        <>
          <Cloud size={14} className="text-fg-subtle" />
          <span className="text-fg-subtle">Saved {formatTime(lastSaved)}</span>
        </>
      )}

      {status === 'error' && (
        <button
          onClick={onSave}
          className="flex items-center gap-1.5 text-red-400 hover:text-red-300"
          title={error || 'Save failed'}
        >
          <CloudOff size={14} />
          <span>Save failed - retry</span>
        </button>
      )}
    </div>
  );
}
